import { PayloadAction } from '@reduxjs/toolkit';
import { call, delay, put, select, takeEvery } from 'redux-saga/effects';
import { SchemaApiValue, SchemaListResponse, TrainedModel } from '../../@shared/Models/Models';
import RespondClient from '../../@shared/Models/RespondClient';
import { SchemaProviderInstance } from '../../@shared/services/SchemaProvider';
import { TrainedModelProviderInstance } from '../../@shared/services/TrainedModelProvider';
import { addOrUpdateTopSchemas, getPage, getPageCount, getTopSchemas, setCountOfSchemas, setIsLoadingOpenedModel, setIsLoadingOpenedSchema, setIsLoadingOpenedSchemaModels, setIsLoadingTopSchemas, setIsModelsLoading, setOpenedModel, setOpenedSchema, setOpenedSchemaModels, setSchemas } from '../../reducers/data/dataReducer';
import { NotificationModel } from '../../reducers/main/mainModel';
import { addAlert, markAsClosedError, removeError } from '../../reducers/main/mainReducer';

import { onThrowAlert, closeErrorSaga, loadSchemas, loadOpenedModel, loadOpenedSchema, loadOpenedSchemaModels, loadTopSchemas, loadOpenedModelByName } from './dataSagaActions';

const dataSaga = [
    takeEvery(loadSchemas, watchLoadingModels),
    takeEvery(loadTopSchemas, watchLoadingTopSchemas),
    takeEvery(onThrowAlert, watchThrowingAlert),
    takeEvery(closeErrorSaga, watchClosingError),
    takeEvery(loadOpenedSchema, watchLoadingOpenedSchema),
    takeEvery(loadOpenedSchemaModels, watchLoadingOpenedSchemaModels),
    takeEvery(loadOpenedModel, watchLoadingOpenedModel),
    takeEvery(loadOpenedModelByName, watchLoadingOpenedModelByName),
]

export default dataSaga;

export function* watchThrowingAlert({ payload: data }: PayloadAction<NotificationModel>): Generator {
    try {
        data.date = new Date();
        yield put(addAlert(data));
        if (data.lifeTime) {
            yield delay(data.lifeTime);
            yield put(closeErrorSaga(data));
        }
    }
    catch (error) {
        yield put(onThrowAlert({
            message: (error as Error).message,
            lifeTime: 10000
        } as NotificationModel));
    }
}

export function* watchClosingError({ payload: data }: PayloadAction<NotificationModel>): Generator {
    yield put(markAsClosedError(data.date));
    yield delay(500);
    yield put(removeError(data.date));
}

export function* watchLoadingModels(): Generator {
    const page = (yield select(getPage)) as number;
    const count = (yield select(getPageCount)) as number;
    yield put(setIsModelsLoading(true));
    const result = (yield call([SchemaProviderInstance, SchemaProviderInstance.getAll], (page - 1) * count, count, "")) as RespondClient<SchemaListResponse>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data.toString(),
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        try {
            yield put(setSchemas(result.data.data));
            yield put(setCountOfSchemas(result.data.count));
        }
        catch (error) {
            yield put(onThrowAlert({
                message: (error as Error).message,
                lifeTime: 10000
            } as NotificationModel));
        }
        finally {
            yield put(setIsModelsLoading(false));
        }
    }
}

export function* watchLoadingOpenedSchema({ payload: data }: PayloadAction<string>): Generator {
    yield put(setIsLoadingOpenedSchema(true));
    const result = (yield call([SchemaProviderInstance, SchemaProviderInstance.get], data)) as RespondClient<SchemaApiValue>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data.toString(),
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        yield put(setOpenedSchema(result.data));
    }
    yield put(setIsLoadingOpenedSchema(false));
}

export function* watchLoadingOpenedSchemaModels({ payload: data }: PayloadAction<string>): Generator {
    yield put(setIsLoadingOpenedSchemaModels(true));
    const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.getAll], data)) as RespondClient<TrainedModel[]>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data.toString(),
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        yield put(setOpenedSchemaModels(result.data));
    }
    yield put(setIsLoadingOpenedSchemaModels(false));
}

export function* watchLoadingOpenedModelByName({ payload: data }: PayloadAction<{ name: string, schema: string }>): Generator {
    yield put(setIsLoadingOpenedModel(true));
    const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.getByName], data.name, data.schema)) as RespondClient<TrainedModel>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data.toString(),
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        yield put(setOpenedModel(result.data));
    }
    yield put(setIsLoadingOpenedModel(false));
}

export function* watchLoadingOpenedModel({ payload: data }: PayloadAction<string>): Generator {
    yield put(setIsLoadingOpenedModel(true));
    const result = (yield call([TrainedModelProviderInstance, TrainedModelProviderInstance.get], data)) as RespondClient<TrainedModel>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data.toString(),
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        yield put(setOpenedModel(result.data));
    }
    yield put(setIsLoadingOpenedModel(false));
}

export function* watchLoadingTopSchemas(): Generator {
    yield put(setIsLoadingTopSchemas(true));
    const schemas = (yield select(getTopSchemas)) as SchemaApiValue[];
    const baseCount = 5;
    const schemasCount = (schemas?.length ?? 0) + baseCount;
    const result = (yield call([SchemaProviderInstance, SchemaProviderInstance.getAll], 0, schemasCount, "")) as RespondClient<SchemaListResponse>;
    if (result.status !== 200) {
        yield put(onThrowAlert({
            message: result.data.toString(),
            lifeTime: 10000
        } as NotificationModel));
    }
    else {
        yield put(addOrUpdateTopSchemas(result.data.data));
        yield put(setCountOfSchemas(result.data.count));
    }
    yield put(setIsLoadingTopSchemas(false));
}